
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Building2, 
  Calendar, 
  Euro, 
  TrendingUp, 
  Leaf, 
  Clock,
  Target,
  BarChart
} from 'lucide-react';
import { AdminSubmission } from '@/hooks/useAdminData';

interface AdminKPICardsProps {
  submissions: AdminSubmission[];
}

const AdminKPICards = ({ submissions }: AdminKPICardsProps) => {
  const kpis = React.useMemo(() => {
    const totalSubmissions = submissions.length;
    const thisMonth = submissions.filter(s => 
      new Date(s.created_at).getMonth() === new Date().getMonth()
    ).length;
    
    const avgEnergyCosts = Math.round(
      submissions.reduce((acc, s) => acc + s.monthly_energy_costs, 0) / totalSubmissions || 0
    );
    
    const totalPotentialSavings = submissions.reduce((acc, s) => 
      acc + (s.yearly_annual_savings || 0), 0
    );
    
    const totalCO2Reduction = submissions.reduce((acc, s) => 
      acc + (s.co2_reduction || 0), 0
    );
    
    const avgROI = Math.round(
      submissions
        .filter(s => s.roi_months)
        .reduce((acc, s) => acc + (s.roi_months || 0), 0) / 
      submissions.filter(s => s.roi_months).length || 0
    );
    
    const avgEnergyScore = Math.round(
      submissions
        .filter(s => s.energy_potential_score)
        .reduce((acc, s) => acc + (s.energy_potential_score || 0), 0) / 
      submissions.filter(s => s.energy_potential_score).length || 0
    );

    const highPotentialSubmissions = submissions.filter(s => 
      (s.energy_potential_score || 0) >= 70
    ).length;

    return {
      totalSubmissions,
      thisMonth,
      avgEnergyCosts,
      totalPotentialSavings,
      totalCO2Reduction,
      avgROI,
      avgEnergyScore,
      highPotentialSubmissions
    };
  }, [submissions]);

  const kpiCards = [
    {
      title: "Totaal Submissions",
      value: kpis.totalSubmissions.toLocaleString(),
      icon: Building2,
      description: "Alle quickscan submissions",
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      title: "Deze Maand",
      value: kpis.thisMonth.toLocaleString(),
      icon: Calendar,
      description: "Nieuwe submissions",
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      title: "Gem. Energiekosten",
      value: `€${kpis.avgEnergyCosts.toLocaleString()}`,
      icon: Euro,
      description: "Per maand gemiddeld",
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    },
    {
      title: "Potentiële Besparingen",
      value: `€${kpis.totalPotentialSavings.toLocaleString()}`,
      icon: TrendingUp,
      description: "Totaal per jaar",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50"
    },
    {
      title: "CO2 Reductie",
      value: `${Math.round(kpis.totalCO2Reduction).toLocaleString()} ton`,
      icon: Leaf,
      description: "Totaal CO2 besparing",
      color: "text-teal-600",
      bgColor: "bg-teal-50"
    },
    {
      title: "Gem. ROI Periode",
      value: `${kpis.avgROI} maanden`,
      icon: Clock,
      description: "Terugverdientijd",
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      title: "Gem. Energy Score",
      value: `${kpis.avgEnergyScore}%`,
      icon: BarChart,
      description: "Gemiddelde potentie score",
      color: "text-indigo-600",
      bgColor: "bg-indigo-50"
    },
    {
      title: "Hoog Potentieel",
      value: kpis.highPotentialSubmissions.toLocaleString(),
      icon: Target,
      description: "Score ≥ 70%",
      color: "text-rose-600",
      bgColor: "bg-rose-50"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {kpiCards.map((kpi, index) => {
        const Icon = kpi.icon;
        return (
          <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {kpi.title}
              </CardTitle>
              <div className={`p-2 rounded-lg ${kpi.bgColor}`}>
                <Icon className={`h-4 w-4 ${kpi.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{kpi.value}</div>
              <p className="text-xs text-gray-500 mt-1">
                {kpi.description}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AdminKPICards;
